<template>
  <div class="server-commands-tab">
    <div class="server-commands-tab__issue-card">
      <div class="server-commands-tab__card-head"><h4>Issue a command</h4></div>
      <div class="server-commands-tab__issue-body">
        <select v-model="selectedType" class="server-commands-tab__select">
          <option value="" disabled>Choose a command…</option>
          <option v-for="type in commandTypes" :key="type.value" :value="type.value">{{ type.label }}</option>
        </select>
        <button
          type="button"
          class="server-commands-tab__btn server-commands-tab__btn--primary"
          :disabled="!selectedType || issuing"
          @click="issue"
        >{{ issuing ? 'Issuing…' : 'Issue command' }}</button>
      </div>
      <p v-if="error" class="server-commands-tab__error">{{ error }}</p>
    </div>

    <div class="server-commands-tab__history-card">
      <div class="server-commands-tab__card-head"><h4>Command history</h4></div>
      <table>
        <thead>
          <tr><th>Command</th><th>Status</th><th>Queued</th><th>Output</th></tr>
        </thead>
        <tbody>
          <tr v-for="cmd in commands" :key="cmd.id">
            <td class="server-commands-tab__mono">{{ cmd.command }}</td>
            <td><span class="server-commands-tab__status" :class="`is-${cmd.status}`">{{ cmd.status }}</span></td>
            <td>{{ formatEpoch(cmd.queued_at) }}</td>
            <td class="server-commands-tab__mono">
              <span v-if="cmd.result_output">{{ cmd.result_output }}{{ cmd.result_truncated ? '…' : '' }}</span>
              <span v-else class="server-commands-tab__na">—</span>
            </td>
          </tr>
          <tr v-if="!commands.length">
            <td colspan="4" class="server-commands-tab__empty">No commands issued yet.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import { formatEpoch } from '@root/services/serverFormat';

// `server-detail.html`'s Commands tab — list + issue ad-hoc, picker populated
// from `GET /api/v1/servers/command-types` (the luna-hub-parity whitelist;
// see `app\module\servers\Enum\CommandType`), since no such listing endpoint
// existed on the servers module before this slice.
const props = defineProps({
  apiBase: { type: String, required: true },
  serverId: { type: [Number, String], required: true },
});

const api = useApi();
const commandTypes = ref([]);
const commands = ref([]);
const selectedType = ref('');
const issuing = ref(false);
const error = ref(null);

async function loadCommandTypes() {
  try {
    const data = await api.get(`${props.apiBase}/command-types`);
    commandTypes.value = data.command_types || [];
  } catch {
    commandTypes.value = [];
  }
}

async function loadHistory() {
  try {
    const data = await api.get(`${props.apiBase}/${props.serverId}/commands`);
    commands.value = data.commands || [];
  } catch {
    commands.value = [];
  }
}

async function issue() {
  if (!selectedType.value) {
    return;
  }
  issuing.value = true;
  error.value = null;
  try {
    await api.post(`${props.apiBase}/${props.serverId}/commands`, { command: selectedType.value, payload: {} });
    selectedType.value = '';
    await loadHistory();
  } catch (err) {
    error.value = err?.response?.data?.error || 'Could not issue that command.';
  } finally {
    issuing.value = false;
  }
}

onMounted(() => {
  loadCommandTypes();
  loadHistory();
});
</script>

<style lang="scss">
.server-commands-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__issue-card, &__history-card {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    overflow: hidden;
  }

  &__card-head {
    padding: 14px 18px 10px;
    border-bottom: 1px solid var(--theme-border-soft);

    h4 {
      margin: 0;
      font-size: 13.5px;
      font-weight: 600;
    }
  }

  &__issue-body {
    display: flex;
    gap: 10px;
    padding: 14px 18px;
  }

  &__select {
    flex: 1;
    padding: 8px 10px;
    border-radius: var(--theme-radius, 6px);
    border: 1px solid var(--theme-border);
    font-size: 12.5px;
  }

  &__btn {
    font-size: 12.5px;
    font-weight: 600;
    padding: 8px 14px;
    border-radius: var(--theme-radius, 6px);
    cursor: pointer;
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);
    white-space: nowrap;

    &--primary {
      background: var(--theme-accent-primary);
      border-color: var(--theme-accent-primary);
      color: #fff;
    }

    &:disabled {
      opacity: .6;
      cursor: default;
    }
  }

  &__error {
    color: #a53232;
    font-size: 12px;
    padding: 0 18px 14px;
    margin: 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th {
    text-align: left;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .05em;
    text-transform: uppercase;
    color: var(--theme-text-tertiary);
    padding: 8px 18px;
    border-bottom: 1px solid var(--theme-border-soft);
  }

  td {
    padding: 8px 18px;
    font-size: 12.5px;
    border-top: 1px solid var(--theme-border-soft);
  }

  &__mono {
    font-family: var(--theme-font-mono);
    font-size: 11.5px;
  }

  &__na {
    color: var(--theme-text-tertiary);
  }

  &__status {
    font-size: 10.5px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
    text-transform: uppercase;
    background: var(--theme-border-soft);
    color: var(--theme-text-secondary);

    &.is-succeeded { background: #e1f3ea; color: #1c7a4d; }
    &.is-failed { background: #f8dede; color: #a53232; }
    &.is-sent { background: #ddeaf4; color: #215d84; }
  }

  &__empty {
    text-align: center;
    color: var(--theme-text-tertiary);
    padding: 16px 18px;
  }
}
</style>
